import React from 'react';
import { upperCase } from 'lodash';
import Input from './Input';

export class CategoryOption extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			loading: false,
		};
	}

	getIcon() {
		let { selected } = this.props.optionData;
		let loading = this.props.optionData.loading || this.state.loading;

		if (loading) {
			return <i className="fas fa-asterisk fa-spin has-text-grey" />;
		}
		if (selected) {
			return <i className="fas fa-check-square has-text-link" />;
		}
		return <i className="far fa-square" />;
	}

	onSelectOption = () => {
		let { optionData, displayKey, dataKey } = this.props;

		this.setState({ loading: true });
		Promise.resolve(
			this.props.onOptionValueChange(!optionData.selected, optionData[dataKey])
		).then(() => this.setState({ loading: false }));
	};

	render() {
		let { optionData, displayKey, dataKey } = this.props;
		return (
			<div
				className={`level category-option mb-1 ${
					optionData.disabled ? 'disable' : ''
				}`}
				onClick={this.onSelectOption}
			>
				<div className="level-left overflow-hidden">
					<div className="level-item">
						<div className="icon">{this.getIcon()}</div>
					</div>
					<div className="level-item overflow-hidden">
						<p className="text-overflow-fade">{optionData[displayKey]}</p>
					</div>
				</div>
			</div>
		);
	}
}

class CategoryBox extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			expanded: ![null, undefined].includes(props.startExpanded)
				? props.startExpanded
				: true,
			loading: false,
		};
	}

	areAllSelected = () =>
		(this.props?.categoryData?.options || []).every((d) => d.selected);

	componentUpdate(prevProps) {
		if (this.props.startExpanded !== prevProps.startExpanded) {
			this.setState({
				expanded: this.props.startExpanded,
			});
		}
	}

	renderBulkSelectOption() {
		let allSelected = this.areAllSelected();
		let icon;
		let text;

		if (this.state.loading) {
			icon = <i className="fas fa-asterisk fa-spin has-text-grey" />;
			text = 'loading...';
		} else if (allSelected) {
			icon = <i className="fas fa-check-square has-text-link" />;
			text = 'Deselect All';
		} else {
			icon = <i className="far fa-square" />;
			text = 'Select All';
		}

		return (
			<div
				className="level category-option mb-4"
				onClick={() => this.bulkSelect()}
			>
				<div className="level-left overflow-hidden">
					<div className="level-item">
						<div className="icon">{icon}</div>
					</div>
					<div className="level-item overflow-hidden">
						<p className="text-overflow-fade">{text}</p>
					</div>
				</div>
			</div>
		);
	}

	bulkSelect = () => {
		this.setState({ loading: true });
		let allSelected = this.areAllSelected();
		Promise.resolve(
			this.props.onBulkOptionValueChange(
				!allSelected,
				this.props.categoryData.options
					.filter((d) => d.selected === allSelected)
					.map((d) => d[this.props.dataKey])
			)
		).then(() => {
			this.setState({ loading: false });
		});
	};

	render() {
		let { categoryData, categoryKey } = this.props;

		return (
			<div className="box category-box">
				<div
					className="level category-box-head overflow-hidden is-clickable"
					onClick={() => this.setState({ expanded: !this.state.expanded })}
				>
					<div className="level-left">
						<div className="level-item">
							<h5 className="is-size-5 has-text-weight-semibold has-text-primary">
								{categoryData.category}
							</h5>
						</div>
					</div>
					<div className="level-right">
						<div className="level-item">
							<span className="icon has-text-primary is-size-5">
								<i
									className={`fas fa-caret-${
										this.state.expanded ? 'down' : 'left'
									}`}
								/>
							</span>
						</div>
					</div>
				</div>

				{this.props.onBulkOptionValueChange && this.renderBulkSelectOption()}

				{this.state.expanded &&
					categoryData.options.map((d) => (
						<CategoryOption
							optionData={{ ...d, loading: d.loading || this.state.loading }}
							{...this.props}
							key={d[this.props.displayKey]}
						/>
					))}
			</div>
		);
	}
}

const HIDDEN_CATEGORIES = [
	'Residential High Value Projects'
];

const GROUP_MAPPING = {
	'Renewable & Efficiency': [
		'Solar, Other Renewables, and Storage',
		'Multiple Building Efficiency Improvements lumped into one scope (assumes no tax benefits)'
	],
	'Efficiency, Air Management, Motors': [
		'Air Heating, Cooling, Flow and Controls',
		'Energy Efficiency',
		'Energy Recovery',
		'Motors and Pumps'
	],
	'Building Envelope and Resiliency': [
		'Building Envelope',
		'Windows, Doors and Openings',
		'Building Resiliency & Infrastructure Abatements'
	],
	'Water & Mechanical': [
		'Water Heating & Treatment',
		'Water Efficiency and Conservation',
		'Pools, Ponds and Lakes',
		'Elevators and Escalators'
	],
	'Transportation, Infrastructure and Safety': [
		'Health & Safety Measures',
		'Building\'s utility infrastructure',
		'Transportation'
	]
};

export const CATEGORY_ORDER = Object.values(GROUP_MAPPING).flat();

export default class SelectByCategory extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			searchVal: '',
			startExpanded: props.startExpanded,
			activeGroup: this.findActiveGroupFromData(props.data) || 'Renewable & Efficiency',
		};
	}

	componentDidMount() {
		if (this.props.useGridSelector && this.props.data && this.props.data.length > 0) {
			const initialActiveGroup = this.findActiveGroupFromData(this.props.data);
			if (initialActiveGroup) {
				this.setState({ activeGroup: initialActiveGroup });
			}
		}
	}

	componentDidUpdate(prevProps) {
		if (this.props.useGridSelector) {
			const wasEmpty = !prevProps.data || prevProps.data.length === 0;
			const isNowPopulated = this.props.data && this.props.data.length > 0;
			
			if ((wasEmpty && isNowPopulated) || (!prevProps.useGridSelector)) {
				const initialActiveGroup = this.findActiveGroupFromData(this.props.data);
				if (initialActiveGroup) {
					this.setState({ activeGroup: initialActiveGroup });
				}
			}
		}
	}

	findActiveGroupFromData = (data) => {
		if (!data || !data.length) return null;
		const selectedOpt = data.find((d) => d.selected);
		if (selectedOpt) {
			let { category } = selectedOpt;
			const name = selectedOpt[this.props.displayKey || 'name'] || '';
			if (name.toLowerCase().includes('biofuel') || name.toLowerCase().includes('bio-fuel')) {
				category = 'Transportation';
			} else if (category === 'Energy Recovery and Bio-fuel') {
				category = 'Energy Recovery';
			}
			for (const [groupName, categories] of Object.entries(GROUP_MAPPING)) {
				if (categories.includes(category)) {
					return groupName;
				}
			}
		}
		return null;
	};

	static defaultProps = {
		data: [],
		categoryKey: 'category', // value by which we split things, also the title of each category
		displayKey: 'name', // value displayed next to checkbox
		dataKey: 'id', // value passed to checkBox onChange function
		startExpanded: false,
		useGridSelector: false,
	};

	getCategorizedData = () => {
		let { data, categoryKey, displayKey, dataKey } = this.props;
		const normalizedData = (data || []).map((item) => {
			const name = item[displayKey] || '';
			let category = item[categoryKey];

			if (name.toLowerCase().includes('biofuel') || name.toLowerCase().includes('bio-fuel')) {
				category = 'Transportation';
			} else if (category === 'Energy Recovery and Bio-fuel') {
				category = 'Energy Recovery';
			}

			return category !== item[categoryKey] ? { ...item, [categoryKey]: category } : item;
		});

		let distinctCats = [...new Set(normalizedData.map((d) => d[categoryKey]))].filter(
			(cat) => !HIDDEN_CATEGORIES.includes(cat)
		);
		
		distinctCats.sort((a, b) => {
			const idxA = CATEGORY_ORDER.indexOf(a);
			const idxB = CATEGORY_ORDER.indexOf(b);
			return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
		});

		let objForRender = [];
		distinctCats.forEach((category) => {
			let options = normalizedData.filter((d) => d[categoryKey] === category);

			if (this.state.searchVal && this.state.searchVal.length > 1) {
				options = options.filter((d) =>
					upperCase(d[displayKey]).includes(upperCase(this.state.searchVal))
				);
			}
			objForRender.push({ category, options });
		});

		return objForRender;
	};

	getGroupedData = (categorizedData) => {
		const groups = {
			'Renewable & Efficiency': [],
			'Efficiency, Air Management, Motors': [],
			'Building Envelope and Resiliency': [],
			'Water & Mechanical': [],
			'Transportation, Infrastructure and Safety': []
		};

		categorizedData.forEach((catObj) => {
			const categoryName = catObj.category;
			let groupFound = false;
			for (const [groupName, categories] of Object.entries(GROUP_MAPPING)) {
				if (categories.includes(categoryName)) {
					groups[groupName].push(catObj);
					groupFound = true;
					break;
				}
			}
			if (!groupFound) {
				groups['Renewable & Efficiency'].push(catObj);
			}
		});

		for (const [groupName, categories] of Object.entries(GROUP_MAPPING)) {
			if (groups[groupName]) {
				groups[groupName].sort((a, b) => {
					const idxA = categories.indexOf(a.category);
					const idxB = categories.indexOf(b.category);
					return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
				});
			}
		}

		return groups;
	};

	renderCategoryBoxes = () => {
		let renderData = this.getCategorizedData();
		let boxes = [];
		return renderData.map((d) => {
			// return <CategoryBox categoryData={d} {...this.props}/>
			return d.options.length ? (
				<CategoryBox
					categoryData={d}
					{...this.props}
					key={d[this.props.categoryKey]}
					startExpanded={this.state.startExpanded}
				/>
			) : (
				<></>
			);
		});
	};

	renderGridSelector() {
		const { displayKey, dataKey } = this.props;
		const categorizedData = this.getCategorizedData();
		const groupedData = this.getGroupedData(categorizedData);
		const GROUPS = [
			'Renewable & Efficiency',
			'Efficiency, Air Management, Motors',
			'Building Envelope and Resiliency',
			'Water & Mechanical',
			'Transportation, Infrastructure and Safety'
		];

		const activeGroup = this.state.activeGroup || GROUPS[0];
		const activeSubgroups = (groupedData[activeGroup] || []).filter(
			(sub) => sub.options && sub.options.length > 0
		);

		const getSearchMatchCount = (group) => {
			const subs = groupedData[group] || [];
			return subs.reduce((acc, sub) => acc + (sub.options ? sub.options.length : 0), 0);
		};

		const hasAnyMatches = GROUPS.some((group) => getSearchMatchCount(group) > 0);

		return (
			<div className="grid-selector-container">
				<div className="level mb-5">
					<div className="level-left">
						{this.props.topLevelLeftHTML}
						<div className="level-item ml-3">
							<Input
								type="text"
								placeholder="search"
								onChange={(searchVal) => {
									this.setState({ searchVal });
								}}
								value={this.state.searchVal}
							/>
						</div>
					</div>
					<div className="level-right">{this.props.topLevelRightHTML}</div>
				</div>

				<div className="is-flex is-flex-wrap-wrap mb-5" style={{ gap: '1rem' }}>
					{GROUPS.map((group) => {
						const isActive = activeGroup === group;
						const matchCount = getSearchMatchCount(group);
						const isSearchActive = this.state.searchVal && this.state.searchVal.length > 1;
						const isDimmed = isSearchActive && matchCount === 0;

						return (
							<div
								key={group}
								className={`card group-tile-card is-clickable ${isActive ? 'is-active-tile' : ''}`}
								style={{
									flex: '1 1 18%',
									minWidth: '160px',
									padding: '1.25rem 1rem',
									borderRadius: '8px',
									textAlign: 'center',
									boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
									transition: 'all 0.2s ease-in-out',
									cursor: 'pointer',
									opacity: isDimmed ? 0.5 : 1,
								}}
								onClick={() => this.setState({ activeGroup: group })}
							>
								<h6
									className="is-size-6 mb-0"
									style={{
										color: isActive ? 'var(--primary-color)' : '#4a4a4a',
										fontWeight: isActive ? '700' : '600',
										fontSize: '0.95rem',
										lineHeight: '1.25'
									}}
								>
									{group}
									{isSearchActive && (
										<span
											className={`tag ml-2 ${matchCount > 0 ? 'is-primary' : 'is-light'}`}
											style={{ borderRadius: '290486px' }}
										>
											{matchCount}
										</span>
									)}
								</h6>
							</div>
						);
					})}
				</div>

				<div className="box p-5 mb-5" style={{ borderRadius: '12px', border: '1px solid #e1e4e6', backgroundColor: '#ffffff' }}>
					{!hasAnyMatches ? (
						<div className="has-text-centered p-5">
							<p className="is-size-5 has-text-grey">No matching project types found.</p>
						</div>
					) : activeSubgroups.length === 0 ? (
						<div className="has-text-centered p-5">
							<p className="is-size-5 has-text-grey">
								No results in this group. Try checking other groups that have matches!
							</p>
						</div>
					) : (
						<div className="columns is-multiline">
							{activeSubgroups.map((subgroup) => (
								<div className="column is-4" key={subgroup.category}>
									<div
										className="box subcard-box"
										style={{
											height: '100%',
											borderRadius: '8px',
											border: '1px solid #e5e5e5',
											backgroundColor: '#fafafa',
											padding: '1.25rem'
										}}
									>
										<h6
											className="is-size-6 has-text-primary has-text-weight-bold mb-3 pb-2"
											style={{
												borderBottom: '1px solid #eef0f2',
												fontSize: '1rem',
												lineHeight: '1.3'
											}}
										>
											{subgroup.category}
										</h6>
										<div className="subgroup-options-list">
											{subgroup.options.map((option) => (
												<CategoryOption
													optionData={option}
													displayKey={displayKey}
													dataKey={dataKey}
													onOptionValueChange={this.props.onOptionValueChange}
													key={option[displayKey]}
												/>
											))}
										</div>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		);
	}

	render() {
		if (this.props.useGridSelector) {
			return this.renderGridSelector();
		}

		return (
			<>
				<div className="level">
					<div className="level-left">
						{this.props.topLevelLeftHTML}
						<div className="level-item">
							<Input
								type="text"
								placeholder="search"
								onChange={(searchVal) => {
									this.setState({
										searchVal,
										startExpanded: !!searchVal.length,
									});
								}}
								value={this.state.searchVal}
							/>
						</div>
					</div>
					<div className="level-right">{this.props.topLevelRightHTML}</div>
				</div>
				<div
					className="select-by-cat-wrapper"
					style={this.props.wrapperStyle || {}}
				>
					{this.renderCategoryBoxes()}
				</div>
			</>
		);
	}
}
